import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Building2, DollarSign } from 'lucide-react';
import { SalesQualificationStepProps } from '@/types/onboarding/salesQualification';
import { formatNumber, createArrayUpdateHelper } from '../utils';

export const FinancialAssessment: React.FC<SalesQualificationStepProps> = ({ data, onUpdate }) => {
  const updateFormData = (field: string, value: unknown) => {
    onUpdate({ ...data, [field]: value });
  };

  const handleArrayUpdate = createArrayUpdateHelper(data, updateFormData);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Financial Position & Assets</h2>
        <p className="text-gray-600">
          Understanding your community's financial health helps us calculate accurate ROI
          projections for enterprise solutions
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Treasury & Finances */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Financial Overview
            </CardTitle>
            <CardDescription>Current financial position and cash flow</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="treasuryAmount">Current Treasury/Reserve Amount *</Label>
              <Input
                id="treasuryAmount"
                type="text"
                inputMode="numeric"
                value={data.treasuryAmount ? formatNumber(data.treasuryAmount.toString()) : ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '');
                  updateFormData('treasuryAmount', raw ? parseInt(raw) : '');
                }}
                placeholder="e.g., 1,500,000"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                This helps us calculate investment potential and ROI projections
              </p>
            </div>

            <div>
              <Label htmlFor="monthlyBudget">Monthly HOA Fee (per unit)</Label>
              <Input
                id="monthlyBudget"
                type="text"
                inputMode="numeric"
                value={data.monthlyBudget ? formatNumber(data.monthlyBudget.toString()) : ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '');
                  updateFormData('monthlyBudget', raw ? parseInt(raw) : '');
                }}
                placeholder="e.g., 350"
              />
            </div>

            <div>
              <Label htmlFor="annualBudgetDetailed">Total Annual Budget</Label>
              <Input
                id="annualBudgetDetailed"
                type="text"
                inputMode="numeric"
                value={
                  data.annualBudgetDetailed
                    ? formatNumber(data.annualBudgetDetailed.toString())
                    : ''
                }
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '');
                  updateFormData('annualBudgetDetailed', raw ? parseInt(raw) : '');
                }}
                placeholder="e.g., 2,500,000"
              />
            </div>

            <div>
              <Label htmlFor="reserveFunds">Emergency/Capital Reserve Funds</Label>
              <Input
                id="reserveFunds"
                type="text"
                inputMode="numeric"
                value={data.reserveFunds ? formatNumber(data.reserveFunds.toString()) : ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '');
                  updateFormData('reserveFunds', raw ? parseInt(raw) : '');
                }}
                placeholder="e.g., 500,000"
              />
            </div>

            <div>
              <Label htmlFor="outstandingDebts">Outstanding Debts/Loans</Label>
              <Input
                id="outstandingDebts"
                type="text"
                inputMode="numeric"
                value={data.outstandingDebts ? formatNumber(data.outstandingDebts.toString()) : ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '');
                  updateFormData('outstandingDebts', raw ? parseInt(raw) : '');
                }}
                placeholder="e.g., 250,000"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="investmentAccounts"
                checked={data.investmentAccounts || false}
                onCheckedChange={(checked) => updateFormData('investmentAccounts', checked)}
              />
              <Label htmlFor="investmentAccounts" className="text-sm">
                Currently have investment accounts (CDs, money market, etc.)
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Community Assets */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Community Assets
            </CardTitle>
            <CardDescription>
              Physical assets and infrastructure owned by the community
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="buildingsOwned">Buildings Owned by Community</Label>
              <Textarea
                id="buildingsOwned"
                value={data.buildingsOwned || ''}
                onChange={(e) => updateFormData('buildingsOwned', e.target.value)}
                placeholder="e.g., Clubhouse, Pool House, Maintenance Facility, Community Center"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="infrastructureOwned">Infrastructure & Utilities Owned</Label>
              <Textarea
                id="infrastructureOwned"
                value={data.infrastructureOwned || ''}
                onChange={(e) => updateFormData('infrastructureOwned', e.target.value)}
                placeholder="e.g., Private roads, Water treatment system, Electrical grid, Fiber network"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="landOwnership">Land Ownership Structure</Label>
              <Select
                value={data.landOwnership || ''}
                onValueChange={(value) => updateFormData('landOwnership', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select ownership type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual_lots">Individual lot ownership</SelectItem>
                  <SelectItem value="common_areas">Common areas only</SelectItem>
                  <SelectItem value="mixed">Mixed ownership structure</SelectItem>
                  <SelectItem value="cooperative">Cooperative ownership</SelectItem>
                  <SelectItem value="master_planned">Master planned community</SelectItem>
                  <SelectItem value="other">Other structure</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="sharedFacilities">Shared Facilities & Amenities</Label>
              <Textarea
                id="sharedFacilities"
                value={data.sharedFacilities || ''}
                onChange={(e) => updateFormData('sharedFacilities', e.target.value)}
                placeholder="e.g., Pool complex, Fitness center, Tennis courts, Golf course, Marina"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="equipmentOwned">Equipment & Vehicles</Label>
              <Textarea
                id="equipmentOwned"
                value={data.equipmentOwned || ''}
                onChange={(e) => updateFormData('equipmentOwned', e.target.value)}
                placeholder="e.g., Maintenance fleet, Pool equipment, Landscaping machinery, Security systems"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Banking Relationships - Enhanced for Enterprise */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-600" />
            Banking & Financial Partnerships
          </CardTitle>
          <CardDescription>
            Current banking partners (critical for Custom Enterprise integration opportunities)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Bank of America',
              'Wells Fargo',
              'Chase',
              'US Bank',
              'PNC Bank',
              'Local Credit Union',
              'Community Bank',
              'Investment Management Firm',
              'Multiple Banking Relationships',
              'Other',
            ].map((bank) => (
              <div key={bank} className="flex items-center space-x-2">
                <Checkbox
                  id={`bank-${bank}`}
                  checked={data.bankingRelationships?.includes(bank) || false}
                  onCheckedChange={(checked) =>
                    handleArrayUpdate('bankingRelationships', bank, !!checked)
                  }
                />
                <Label htmlFor={`bank-${bank}`} className="text-sm">
                  {bank}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Financial Pain Points */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Financial Management Challenges</CardTitle>
          <CardDescription>
            What financial pain points are you currently experiencing?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'Lack of investment returns on reserves',
              'High banking fees and transaction costs',
              'Difficulty accessing capital for improvements',
              'Poor financial reporting and transparency',
              'Inefficient payment collection processes',
              'Limited banking product options',
              'Challenges with large project financing',
              'Insufficient reserve fund growth',
              'Complex multi-entity financial management',
              'Regulatory compliance costs',
            ].map((painPoint) => (
              <div key={painPoint} className="flex items-center space-x-2">
                <Checkbox
                  id={`financial-pain-${painPoint}`}
                  checked={data.financialPainPoints?.includes(painPoint) || false}
                  onCheckedChange={(checked) =>
                    handleArrayUpdate('financialPainPoints', painPoint, !!checked)
                  }
                />
                <Label htmlFor={`financial-pain-${painPoint}`} className="text-sm">
                  {painPoint}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ROI Preview - Enhanced for Enterprise */}
      {data.treasuryAmount && parseInt(data.treasuryAmount.toString()) > 0 && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <h4 className="font-semibold text-green-800 mb-2">Enterprise ROI Preview</h4>
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <div className="text-lg font-bold text-green-700">
                    ${(parseInt(data.treasuryAmount.toString()) * 0.06).toLocaleString()}
                  </div>
                  <div className="text-sm text-green-600">Enhanced Investment Returns</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-green-700">
                    $
                    {(
                      (parseInt(data.monthlyBudget?.toString() || '0') || 0) *
                      12 *
                      (parseInt(data.organizationSize?.toString().replace(/,/g, '') || '0') || 0) *
                      0.2
                    ).toLocaleString()}
                  </div>
                  <div className="text-sm text-green-600">Operational Savings</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-green-700">
                    $
                    {(
                      (parseInt(data.organizationSize?.toString().replace(/,/g, '') || '0') || 0) *
                      25
                    ).toLocaleString()}
                  </div>
                  <div className="text-sm text-green-600">Banking Fee Savings</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-green-700">
                    $
                    {(
                      parseInt(data.treasuryAmount.toString()) * 0.06 +
                      (parseInt(data.monthlyBudget?.toString() || '0') || 0) *
                        12 *
                        (parseInt(data.organizationSize?.toString().replace(/,/g, '') || '0') ||
                          0) *
                        0.2 +
                      (parseInt(data.organizationSize?.toString().replace(/,/g, '') || '0') || 0) *
                        25
                    ).toLocaleString()}
                  </div>
                  <div className="text-sm text-green-600">Total Annual Enterprise Value</div>
                </div>
              </div>
              <p className="text-xs text-green-600 mt-2">
                *Conservative enterprise estimates: 6% enhanced returns, 20% operational savings,
                $25/unit banking optimization
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Help Text */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <p className="text-sm text-purple-700">
          <strong>Why this financial data matters for Enterprise:</strong>
        </p>
        <ul className="text-sm text-purple-700 mt-2 space-y-1">
          <li>• Calculate custom ROI projections and business case development</li>
          <li>• Identify enterprise-level investment and banking optimization opportunities</li>
          <li>• Structure implementation financing and phased rollout plans</li>
          <li>• Design custom banking partnerships and white-label financial solutions</li>
          <li>• Assess capacity for comprehensive digital transformation initiatives</li>
        </ul>
      </div>
    </div>
  );
};
